/**
 * API admin pour la gestion du portail étudiant (paiements + accès).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface ImportPaiementResume {
  id: number;
  periode: string;
  fichier_nom: string;
  date_import: string;
  importe_par_nom: string;
  nb_etudiants: number;
  nb_a_jour: number;
  nb_en_dette: number;
  total_attendu: string;
  total_paye: string;
  total_du: string;
}

export interface PreviewPaiementLigne {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  frais_attendus: string;
  frais_payes: string;
  solde: string;
  est_a_jour: boolean;
}

export interface DiffNouveau {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  frais_payes_nouveau: string;
}

export interface DiffMaj {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  frais_payes_avant: string;
  frais_payes_apres: string;
  delta_paye: string;
  solde_avant: string;
  solde_apres: string;
  a_jour_avant: boolean;
  a_jour_apres: boolean;
}

export interface DiffConserve {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  frais_payes: string;
  solde: string;
  est_a_jour: boolean;
}

export interface DiffAlertePole {
  matricule: string;
  nom: string;
  prenoms: string;
  pole_existant: string;
  pole_fichier: string;
}

export interface PreviewPaiementResponse {
  preview: true;
  nb_lignes: number;
  nb_a_jour: number;
  nb_en_dette: number;
  totaux: { attendu: string; paye: string; du: string };
  echantillon: PreviewPaiementLigne[];
  header_row: number;
  pole_detecte?: string;
  pole_propose?: string;
  seuil_applique?: string | null;
  diff?: {
    totaux: {
      nb_nouveaux: number;
      nb_maj: number;
      nb_inchanges: number;
      nb_conserves: number;
      nb_alertes_pole: number;
    };
    nouveaux_apercu: DiffNouveau[];
    maj_montants_apercu: DiffMaj[];
    conserves_apercu: DiffConserve[];
    alertes_pole_apercu: DiffAlertePole[];
  };
}

export interface ApplyPaiementResponse {
  success: true;
  periode: string;
  import_id: number;
  backup_path: string;
  pole?: string;
  mode?: 'fusion' | 'remplacer';
  nb_lignes: number;
  nb_a_jour: number;
  nb_en_dette: number;
  nb_crees?: number;
  nb_majs?: number;
  nb_supprimes_avant_insert?: number;
  nb_pole_force?: number;
  nb_conserves?: number;
  nb_alertes_pole?: number;
  totaux: { attendu: string; paye: string; du: string };
}

export interface EtudiantAcces {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  pole?: string;
  niveau?: string;
  option?: string;
  type_formation?: string;
  mode: 'a_jour' | 'force_admin' | 'sous_contrat' | 'bloque_dette' | 'bloque_absent';
  autorise: boolean;
  solde_total: string;
  nb_a_jour: number;
  nb_periodes_requises: number;
  compte_cree: boolean;
  last_login: string | null;
  derniere_activite?: string | null;
  derniere_ip?: string | null;
  nb_visites?: number;
  acces_force: boolean;
  bloque?: boolean;
  bloque_raison?: string;
  sous_contrat?: boolean;
  contrat_type?: string;
  contrat_remarque?: string;
  contrat_par_nom?: string;
  // Aménagement de paiement de l'année active (2026-09-12)
  amenagement?: boolean;
  periodes_exemptees?: string[];
  // Accès léger (2026-09-12)
  acces_leger?: boolean;
  // Accès léger accordé individuellement (2026-09-13)
  acces_leger_individuel?: boolean;
}

// ── État de paiement détaillé + aménagement (2026-09-12) ──────────────────
export type StatutPeriodePaiement = 'a_jour' | 'reliquat' | 'impaye' | 'non_enregistre' | 'non_concerne';
export interface PeriodePaiementEtat {
  periode: string;
  label: string;
  requise_globale: boolean;
  concernee: boolean;
  requise: boolean;
  present: boolean;
  frais_attendus: string;
  frais_payes: string;
  solde_annuel: string;
  seuil_a_jour: string | null;
  montant_exige: string | null;
  attendu_effectif: string;
  reste: string;
  est_a_jour: boolean;
  statut: StatutPeriodePaiement;
  saisie_manuelle: boolean;
  saisie_par_nom: string;
  date_maj: string | null;
}
export interface AmenagementPaiementInfo {
  id: number;
  annee_academique: string;
  periodes_exemptees: string[];
  montants_exiges: Record<string, string>;
  motif: string;
  modifie_par_nom: string;
  date_modification: string | null;
}
export interface AccesLegerIndividuelInfo {
  annee_academique: string;
  motif: string;
  par_nom: string;
  date: string | null;
}
export interface EtatPaiementEtudiantResponse {
  matricule: string;
  annee_academique: string;
  etudiant: {
    matricule: string; nom: string; prenoms: string; classe: string;
    niveau: string; pole: string; type_formation: string;
  } | null;
  periodes: PeriodePaiementEtat[];
  totaux: { attendu: string; paye: string; reste: string };
  nb_periodes_a_regler: number;
  nb_periodes_requises_a_regler: number;
  amenagement: AmenagementPaiementInfo | null;
  acces_leger_individuel?: AccesLegerIndividuelInfo | null;
  acces: {
    autorise: boolean; mode: EtudiantAcces['mode']; solde_total: string;
    nb_periodes_requises: number; nb_a_jour: number;
    acces_leger?: boolean; periodes_leger_dues?: string[];
  };
  periodes_requises_globales: string[];
  sous_contrat: boolean;
  acces_force: boolean;
  /** Reçus encaissés à la caisse (2026-09-16) : corrigeables et annulables */
  encaissements?: EncaissementManuelInfo[];
}

/** Un reçu encaissé à la caisse, avec sa traçabilité (2026-09-16). */
export interface EncaissementManuelInfo {
  id: number;
  matricule: string;
  periode: string;
  annee_academique: string;
  montant: string;
  montant_attendu: string | null;
  reference_recu: string;
  remarque: string;
  statut: 'actif' | 'annule';
  saisi_par_nom: string;
  date_saisie: string | null;
  modifie_par_nom: string;
  date_modification: string | null;
  motif_modification: string;
  annule_par_nom: string;
  date_annulation: string | null;
  motif_annulation: string;
}

export interface KpisTempsReel {
  nb_en_ligne: number;
  nb_bloques: number;
  nb_sous_contrat?: number;
  nb_comptes_actifs: number;
  matricules_en_ligne: string[];
  seuil_online_min: number;
}

export interface ConnexionEvent {
  id: number;
  event: 'login_ok' | 'login_fail' | 'login_bloque' | 'logout';
  event_label: string;
  ip: string;
  user_agent: string;
  date: string;
}

export interface AccesAgregations {
  par_pole: { pole: string; nb: number }[];
  par_niveau: { niveau: string; nb: number }[];
  classes: string[];
}

export interface MessageReclamation {
  id: number;
  expediteur: 'etudiant' | 'scolarite' | 'systeme';
  expediteur_nom: string;
  contenu: string;
  date_creation: string;
}

export interface ReclamationKpis {
  total: number;
  deposees: number;
  en_examen: number;
  acceptees: number;
  rejetees: number;
  cloturees: number;
  avec_messages_non_lus: number;
}

export interface PeriodesConfig {
  periodes_requises: string[];
  // Accès léger (2026-09-12) : décision visible sans notes si seules ces périodes sont impayées
  periodes_acces_leger?: string[];
  periodes_disponibles: { code: string; label: string }[];
  poles_disponibles: { code: string; label: string }[];
  /** Par type de formation (2026-10-03). configuree=false → reprend la liste générale */
  categories?: PeriodesCategorie[];
}

export type CategorieFormation = 'FI' | 'CS' | 'MASTER';
export interface PeriodesCategorie {
  code: CategorieFormation;
  label: string;
  configuree: boolean;
  periodes_requises: string[];
  periodes_acces_leger: string[];
  nb_etudiants: number;
}

export interface ActualiteAdminItem {
  id: number;
  type: string;
  type_label: string;
  titre: string;
  contenu: string;
  image_url: string | null;
  lien_externe: string;
  date_evenement: string | null;
  lieu: string;
  date_creation: string;
  date_modification?: string;
  auteur_nom: string;
  epinglee: boolean;
  publiee: boolean;
  reactions: Record<string, number>;
  nb_reactions_total: number;
  ma_reaction: string | null;
  a_lu: boolean;
  tous_etudiants: boolean;
  niveaux_cibles: string[];
  classes_cibles: string[];
  options_cibles: string[];
  poles_cibles?: string[];
  audience?: 'tous' | 'etudiants' | 'parents';
  service_emetteur?: string;
  nb_lues?: number;
}

export const portailAdminApi = createApi({
  reducerPath: 'portailAdminApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Imports', 'Acces', 'Config', 'Reclamations', 'Maintenance', 'BlocageNotes', 'Actualites'],
  endpoints: (builder) => ({
    importPaiementPreview: builder.mutation<PreviewPaiementResponse, FormData>({
      query: (formData) => ({
        url: '/portail/admin/import-paiements/',
        method: 'POST',
        body: formData,
      }),
    }),
    importPaiementApply: builder.mutation<ApplyPaiementResponse, FormData>({
      query: (formData) => ({
        url: '/portail/admin/import-paiements/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Imports', 'Acces'],
    }),
    listImports: builder.query<{ results: ImportPaiementResume[] }, void>({
      query: () => '/portail/admin/imports-paiements/',
      providesTags: ['Imports'],
    }),
    listEtudiantsAcces: builder.query<
      { results: EtudiantAcces[]; total: number; total_filtres?: number; agregations: AccesAgregations; kpis_temps_reel: KpisTempsReel },
      { classe?: string; statut?: string; search?: string; pole?: string; niveau?: string; type_formation?: string; limit?: number }
    >({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params.classe) usp.set('classe', params.classe);
        if (params.statut) usp.set('statut', params.statut);
        if (params.search) usp.set('search', params.search);
        if (params.pole) usp.set('pole', params.pole);
        if (params.niveau) usp.set('niveau', params.niveau);
        if (params.type_formation) usp.set('type_formation', params.type_formation);
        if (params.limit) usp.set('limit', String(params.limit));
        return `/portail/admin/etudiants-acces/?${usp.toString()}`;
      },
      providesTags: ['Acces'],
    }),
    forcerAcces: builder.mutation<
      { success: true; matricule: string; acces_force: boolean; raison: string },
      { matricule: string; activer: boolean; raison: string; mot_de_passe?: string }
    >({
      query: ({ matricule, activer, raison, mot_de_passe }) => ({
        url: `/portail/admin/etudiants/${matricule}/forcer-acces/`,
        method: 'POST',
        body: { activer, raison, mot_de_passe },
      }),
      invalidatesTags: ['Acces'],
    }),
    getPeriodesConfig: builder.query<PeriodesConfig, void>({
      query: () => '/portail/admin/periodes-requises/',
      providesTags: ['Config'],
    }),
    setPeriodesRequises: builder.mutation<
      { success: true; categorie?: string; periodes_requises: string[]; periodes_acces_leger?: string[] },
      { periodes: string[]; periodes_acces_leger?: string[]; categorie?: CategorieFormation }
    >({
      query: (body) => ({
        url: '/portail/admin/periodes-requises/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Config', 'Acces'],
    }),
    listReclamationsAdmin: builder.query<
      {
        results: ReclamationAdminItem[];
        kpis: ReclamationKpis;
        par_pole: { pole: string; nb: number }[];
        restriction_pole: { restreint: boolean; poles_autorises: string[] };
      },
      {
        statut?: string;
        type?: string;
        pole?: string;
        niveau?: string;
        classe?: string;
        search?: string;
        semestre_parite?: 'pair' | 'impair';
        type_session?: 'ordinaire' | 'rattrapage';
      }
    >({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params.statut) usp.set('statut', params.statut);
        if (params.type) usp.set('type', params.type);
        if (params.pole) usp.set('pole', params.pole);
        if (params.niveau) usp.set('niveau', params.niveau);
        if (params.classe) usp.set('classe', params.classe);
        if (params.search) usp.set('search', params.search);
        if (params.semestre_parite) usp.set('semestre_parite', params.semestre_parite);
        if (params.type_session) usp.set('type_session', params.type_session);
        return `/portail/admin/reclamations/?${usp.toString()}`;
      },
      providesTags: ['Reclamations'],
    }),
    traiterReclamation: builder.mutation<
      { success: true; statut: string; date_traitement: string },
      { id: number; action: 'en_examen' | 'acceptee' | 'rejetee'; reponse?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/portail/admin/reclamations/${id}/traiter/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Reclamations'],
    }),
    getReclamationMessages: builder.query<
      { results: MessageReclamation[]; reclamation: { id: number; statut: string; cloturee: boolean; matricule: string; matiere_nom: string } },
      number
    >({
      query: (id) => `/portail/admin/reclamations/${id}/messages/`,
      providesTags: (_r, _e, id) => [{ type: 'Reclamations', id: `msg-${id}` }],
    }),
    sendReclamationMessage: builder.mutation<
      { results: MessageReclamation[] },
      { id: number; contenu: string }
    >({
      query: ({ id, contenu }) => ({
        url: `/portail/admin/reclamations/${id}/messages/`,
        method: 'POST',
        body: { contenu },
      }),
      invalidatesTags: (_r, _e, { id }) => ['Reclamations', { type: 'Reclamations', id: `msg-${id}` }],
    }),
    cloturerReclamation: builder.mutation<
      { success: true; statut: string },
      { id: number; raison?: string }
    >({
      query: ({ id, raison }) => ({
        url: `/portail/admin/reclamations/${id}/cloturer/`,
        method: 'POST',
        body: { raison },
      }),
      invalidatesTags: (_r, _e, { id }) => ['Reclamations', { type: 'Reclamations', id: `msg-${id}` }],
    }),
    getReclamationQuickEdit: builder.query<
      {
        reclamation: {
          id: number; matricule: string; matiere_id: number; matiere_nom: string;
          matiere_code: string; type_reclamation: string;
          note_actuelle: string | null; note_attendue: string | null;
          motif: string; statut: string; cloturee: boolean;
        };
        etudiant: { matricule: string; nom_complet: string; classe: string };
        notes_actuelles: {
          devoir: {
            existe: boolean; note1: string | null; note2: string | null;
            absent1: boolean; absent2: boolean;
            moyenne_devoirs: string | null; verrouille: boolean;
          };
          examen: {
            existe: boolean; note_examen: string | null; absent_examen: boolean; verrouille?: boolean;
          };
          rattrapage: {
            existe: boolean; note_examen: string | null; absent_examen?: boolean; verrouille?: boolean;
          };
          /** Note d'examen qui compte : la meilleure des deux sessions (2026-10-05) */
          note_retenue?: 'SO' | 'SR' | null;
          moyenne_ecue?: string | null;
        };
        session_reclamation?: 'ordinaire' | 'rattrapage';
        scans: Array<{
          type: string; libelle: string; image_base64: string;
          numero_ligne: number | null; code_releve: string;
        }>;
      },
      number
    >({
      query: (id) => `/portail/admin/reclamations/${id}/quick-edit/`,
    }),
    applyReclamationQuickEdit: builder.mutation<
      { success: true; reclamation_id: number; changements: string[]; statut: string },
      {
        id: number;
        commentaire: string;
        note1?: string | null;
        absent1?: boolean;
        note2?: string | null;
        absent2?: boolean;
        note_examen?: string | null;
        absent_examen?: boolean;
        note_rattrapage?: string | null;
        absent_rattrapage?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/portail/admin/reclamations/${id}/quick-edit/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => ['Reclamations', { type: 'Reclamations', id: `msg-${id}` }],
    }),
    rouvrirReclamation: builder.mutation<
      { success: true; statut: string },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/portail/admin/reclamations/${id}/rouvrir/`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { id }) => ['Reclamations', { type: 'Reclamations', id: `msg-${id}` }],
    }),
    getPublicationConfig: builder.query<
      { fenetre: any; jours_reclamation: number },
      void
    >({
      query: () => '/portail/admin/publication/',
      providesTags: ['Config'],
    }),
    setPublicationConfig: builder.mutation<
      { fenetre: any; jours_reclamation: number },
      { date_publication?: string; jours_reclamation?: number }
    >({
      query: (body) => ({
        url: '/portail/admin/publication/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Config'],
    }),
    togglePublication: builder.mutation<
      { success: true; action: 'ouvertes' | 'fermees'; fenetre: any },
      void
    >({
      query: () => ({
        url: '/portail/admin/publication/toggle/',
        method: 'POST',
      }),
      invalidatesTags: ['Config'],
    }),
    resetPasswordEtudiant: builder.mutation<
      { success: true; matricule: string; nouveau_password: string; message: string },
      { matricule: string; raison?: string }
    >({
      query: ({ matricule, raison }) => ({
        url: `/portail/admin/etudiants/${matricule}/reset-password/`,
        method: 'POST',
        body: { raison: raison || '' },
      }),
      invalidatesTags: ['Acces'],
    }),
    bloquerEtudiant: builder.mutation<
      { success: true; matricule: string; bloque: boolean; raison: string },
      { matricule: string; bloquer: boolean; raison?: string }
    >({
      query: ({ matricule, bloquer, raison }) => ({
        url: `/portail/admin/etudiants/${matricule}/bloquer/`,
        method: 'POST',
        body: { bloquer, raison: raison || '' },
      }),
      invalidatesTags: ['Acces'],
    }),
    marquerContrat: builder.mutation<
      { success: true; matricule: string; sous_contrat: boolean; contrat_type: string; contrat_remarque: string },
      { matricule: string; actif: boolean; type?: string; remarque?: string }
    >({
      query: ({ matricule, actif, type, remarque }) => ({
        url: `/portail/admin/etudiants/${matricule}/contrat/`,
        method: 'POST',
        body: { actif, type: type || '', remarque: remarque || '' },
      }),
      invalidatesTags: ['Acces'],
    }),
    encaisserPaiement: builder.mutation<
      {
        success: true;
        matricule: string;
        periode: string;
        action: string;
        reference_recu: string;
        montant_paye: string;
        montant_attendu: string;
        solde: string;
        est_a_jour: boolean;
        enregistre_par: string;
      },
      {
        matricule: string;
        periode: string;
        montant_paye: number | string;
        reference_recu: string;
        montant_attendu?: number | string;
        remarque?: string;
      }
    >({
      query: ({ matricule, ...body }) => ({
        url: `/portail/admin/etudiants/${matricule}/encaisser-paiement/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Acces'],
    }),
    // Encaissements de caisse (2026-09-16) : liste, correction, annulation
    getEncaissements: builder.query<{ matricule: string; encaissements: EncaissementManuelInfo[] }, string>({
      query: (matricule) => `/portail/admin/etudiants/${matricule}/encaissements/`,
      providesTags: (_r, _e, matricule) => [{ type: 'Acces', id: `encaissements-${matricule}` }],
    }),
    modifierEncaissement: builder.mutation<
      { success: true; encaissement: EncaissementManuelInfo; etat_paiement: EtatPaiementEtudiantResponse },
      {
        id: number; matricule: string; motif: string;
        montant?: number; periode?: string; reference_recu?: string;
        remarque?: string; montant_attendu?: number;
      }
    >({
      query: ({ id, matricule, ...body }) => ({
        url: `/portail/admin/encaissements/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { matricule }) => [
        'Acces',
        { type: 'Acces', id: `paiement-${matricule}` },
        { type: 'Acces', id: `encaissements-${matricule}` },
      ],
    }),
    annulerEncaissement: builder.mutation<
      { success: true; encaissement: EncaissementManuelInfo; etat_paiement: EtatPaiementEtudiantResponse },
      { id: number; matricule: string; motif: string }
    >({
      query: ({ id, motif }) => ({
        url: `/portail/admin/encaissements/${id}/annuler/`,
        method: 'POST',
        body: { motif },
      }),
      invalidatesTags: (_r, _e, { matricule }) => [
        'Acces',
        { type: 'Acces', id: `paiement-${matricule}` },
        { type: 'Acces', id: `encaissements-${matricule}` },
      ],
    }),
    getEtatPaiementEtudiant: builder.query<EtatPaiementEtudiantResponse, string>({
      query: (matricule) => `/portail/admin/etudiants/${matricule}/etat-paiement/`,
      providesTags: (_r, _e, matricule) => [{ type: 'Acces', id: `paiement-${matricule}` }],
    }),
    enregistrerAmenagement: builder.mutation<
      EtatPaiementEtudiantResponse,
      { matricule: string; periodes_exemptees: string[]; montants_exiges: Record<string, string>; motif: string }
    >({
      query: ({ matricule, ...body }) => ({
        url: `/portail/admin/etudiants/${matricule}/amenagement/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Acces'],
    }),
    retirerAmenagement: builder.mutation<EtatPaiementEtudiantResponse, { matricule: string; motif?: string }>({
      query: ({ matricule, ...body }) => ({
        url: `/portail/admin/etudiants/${matricule}/amenagement/`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Acces'],
    }),
    // Accès léger individuel aux résultats (2026-09-13)
    definirAccesLeger: builder.mutation<
      EtatPaiementEtudiantResponse,
      { matricule: string; actif: boolean; motif?: string }
    >({
      query: ({ matricule, ...body }) => ({
        url: `/portail/admin/etudiants/${matricule}/acces-leger/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Acces'],
    }),
    getHistoriqueConnexions: builder.query<
      {
        matricule: string;
        results: ConnexionEvent[];
        stats: { nb_total: number; nb_ok: number; nb_fail: number };
        compte: { derniere_activite: string | null; last_login: string | null; bloque: boolean; bloque_raison: string } | null;
      },
      string
    >({
      query: (matricule) => `/portail/admin/etudiants/${matricule}/historique-connexions/`,
    }),
    notifierEtudiant: builder.mutation<
      { success: true; notification_id: number },
      { matricule: string; titre: string; message: string; type?: string; lien?: string }
    >({
      query: ({ matricule, ...body }) => ({
        url: `/portail/admin/etudiants/${matricule}/notifier/`,
        method: 'POST',
        body,
      }),
    }),
    getMaintenance: builder.query<
      { actif: boolean; message: string; depuis: string | null; par_nom: string },
      void
    >({
      query: () => '/portail/admin/maintenance/',
      providesTags: ['Maintenance'],
    }),
    setMaintenance: builder.mutation<
      { actif: boolean; message: string; depuis: string | null; par_nom: string },
      { actif: boolean; message?: string }
    >({
      query: (body) => ({
        url: '/portail/admin/maintenance/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Maintenance'],
    }),
    getBlocageNotes: builder.query<
      {
        actif: boolean; message: string; depuis: string | null; par_nom: string;
        liberations: { pole: string; niveau: string; classe: string; semestre?: 'S1' | 'S2' | '' }[];
      },
      void
    >({
      query: () => '/portail/admin/blocage-notes/',
      providesTags: ['BlocageNotes'],
    }),
    setBlocageNotes: builder.mutation<
      {
        actif: boolean; message: string; depuis: string | null; par_nom: string;
        liberations: { pole: string; niveau: string; classe: string }[];
      },
      {
        actif: boolean;
        message?: string;
        liberations?: { pole?: string; niveau?: string; classe?: string }[];
      }
    >({
      query: (body) => ({
        url: '/portail/admin/blocage-notes/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BlocageNotes'],
    }),
    libererBlocageNotes: builder.mutation<
      {
        actif: boolean;
        liberations: { pole: string; niveau: string; classe: string; semestre?: string }[];
      },
      { pole?: string; niveau?: string; classe?: string; semestre?: 'S1' | 'S2' | '' }
    >({
      query: (body) => ({
        url: '/portail/admin/blocage-notes/liberer/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BlocageNotes'],
    }),
    rebloquerBlocageNotes: builder.mutation<
      {
        actif: boolean;
        liberations: { pole: string; niveau: string; classe: string; semestre?: string }[];
      },
      { pole?: string; niveau?: string; classe?: string; semestre?: 'S1' | 'S2' | '' }
    >({
      query: (body) => ({
        url: '/portail/admin/blocage-notes/rebloquer/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BlocageNotes'],
    }),
    getLimiteReclamations: builder.query<{ limite: number }, void>({
      query: () => '/portail/admin/limite-reclamations/',
      providesTags: ['Config'],
    }),
    setLimiteReclamations: builder.mutation<{ success: true; limite: number }, { limite: number }>({
      query: (body) => ({
        url: '/portail/admin/limite-reclamations/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Config'],
    }),
    listActualitesAdmin: builder.query<
      { results: ActualiteAdminItem[]; total: number },
      { statut?: string; type?: string; auteur_role?: string; audience?: string }
    >({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params.statut) usp.set('statut', params.statut);
        if (params.type) usp.set('type', params.type);
        if (params.auteur_role) usp.set('auteur_role', params.auteur_role);
        if (params.audience) usp.set('audience', params.audience);
        return `/portail/admin/actualites/?${usp.toString()}`;
      },
      providesTags: ['Actualites'],
    }),
    createActualite: builder.mutation<ActualiteAdminItem, FormData>({
      query: (formData) => ({
        url: '/portail/admin/actualites/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Actualites'],
    }),
    updateActualite: builder.mutation<ActualiteAdminItem, { id: number; data: FormData }>({
      query: ({ id, data }) => ({
        url: `/portail/admin/actualites/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Actualites'],
    }),
    deleteActualite: builder.mutation<{ success: true }, number>({
      query: (id) => ({
        url: `/portail/admin/actualites/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Actualites'],
    }),
    broadcastMessage: builder.mutation<
      { success: true; broadcast_id: string; nb_destinataires: number; titre: string },
      {
        titre: string;
        message: string;
        type?: string;
        lien?: string;
        filtres?: { pole?: string; niveau?: string; classe?: string; type_formation?: string };
        matricules?: string[];
      }
    >({
      query: (body) => ({
        url: '/portail/admin/notifications/broadcast/',
        method: 'POST',
        body,
      }),
    }),
    listBroadcastsRecents: builder.query<
      {
        results: Array<{
          broadcast_id: string;
          titre: string;
          message_extrait: string;
          type_notif: string;
          date_envoi: string;
          nb_destinataires: number;
          nb_lus: number;
          nb_non_lus: number;
        }>;
        total: number;
      },
      void
    >({
      query: () => '/portail/admin/notifications/broadcasts/',
    }),
    annulerBroadcast: builder.mutation<
      { success: true; nb_supprimees: number; titre: string; avertissement: string },
      string
    >({
      query: (broadcastId) => ({
        url: `/portail/admin/notifications/broadcasts/${broadcastId}/annuler/`,
        method: 'POST',
      }),
    }),

    // Liste des étudiants actuellement connectés au portail (poll temps réel)
    getEtudiantsConnectes: builder.query<EtudiantsConnectesResponse, EtudiantsConnectesParams>({
      query: (params) => ({
        url: '/portail/admin/etudiants-connectes/',
        params: params ?? undefined,
      }),
    }),
  }),
});

export interface EtudiantConnecteItem {
  matricule: string;
  nom: string;
  prenoms: string;
  nom_complet: string;
  classe: string;
  niveau: string;
  option: string;
  pole: string;
  photo_url: string | null;
  derniere_activite: string | null;
  nb_visites: number;
}

export interface EtudiantsConnectesResponse {
  results: EtudiantConnecteItem[];
  total: number;
  fenetre_minutes: number;
  par_classe: { classe: string; nb: number }[];
  par_niveau: { niveau: string; nb: number }[];
}

export interface EtudiantsConnectesParams {
  fenetre_minutes?: number;
  classe?: string;
  niveau?: string;
  option?: string;
  pole?: string;
  q?: string;
}

export interface ReclamationAdminItem {
  id: number;
  matricule: string;
  etudiant: { nom: string; prenoms: string; classe: string; niveau?: string; pole?: string; option: string };
  type_reclamation: 'devoir' | 'examen' | 'autre';
  matiere_id: number | null;
  matiere_nom: string;
  semestre: string;
  note_actuelle: string | null;
  note_attendue: string | null;
  motif: string;
  statut: 'deposee' | 'en_examen' | 'acceptee' | 'rejetee' | 'cloturee';
  cloturee?: boolean;
  reponse_scolarite: string;
  traite_par_nom: string;
  date_creation: string;
  date_traitement: string | null;
  derniere_activite?: string;
  nb_messages?: number;
  nb_non_lus_admin?: number;
}

export const {
  useImportPaiementPreviewMutation,
  useImportPaiementApplyMutation,
  useListImportsQuery,
  useListEtudiantsAccesQuery,
  useForcerAccesMutation,
  useGetPeriodesConfigQuery,
  useSetPeriodesRequisesMutation,
  useListReclamationsAdminQuery,
  useTraiterReclamationMutation,
  useGetReclamationMessagesQuery,
  useSendReclamationMessageMutation,
  useCloturerReclamationMutation,
  useRouvrirReclamationMutation,
  useGetReclamationQuickEditQuery,
  useApplyReclamationQuickEditMutation,
  useGetPublicationConfigQuery,
  useSetPublicationConfigMutation,
  useTogglePublicationMutation,
  useResetPasswordEtudiantMutation,
  useBloquerEtudiantMutation,
  useMarquerContratMutation,
  useEncaisserPaiementMutation,
  useGetEncaissementsQuery,
  useModifierEncaissementMutation,
  useAnnulerEncaissementMutation,
  useGetEtatPaiementEtudiantQuery,
  useEnregistrerAmenagementMutation,
  useRetirerAmenagementMutation,
  useDefinirAccesLegerMutation,
  useGetHistoriqueConnexionsQuery,
  useLazyGetHistoriqueConnexionsQuery,
  useNotifierEtudiantMutation,
  useBroadcastMessageMutation,
  useListBroadcastsRecentsQuery,
  useLazyListBroadcastsRecentsQuery,
  useAnnulerBroadcastMutation,
  useGetMaintenanceQuery,
  useSetMaintenanceMutation,
  useGetBlocageNotesQuery,
  useSetBlocageNotesMutation,
  useLibererBlocageNotesMutation,
  useRebloquerBlocageNotesMutation,
  useGetLimiteReclamationsQuery,
  useSetLimiteReclamationsMutation,
  useListActualitesAdminQuery,
  useCreateActualiteMutation,
  useUpdateActualiteMutation,
  useDeleteActualiteMutation,
  useGetEtudiantsConnectesQuery,
} = portailAdminApi;
